import { useState, useEffect, useRef } from 'react';
import { motion } from 'framer-motion';
import { Html5Qrcode } from 'html5-qrcode';
import axios from 'axios';
import { toast } from 'sonner';
import { X, Gift, Check, Loader2, ScanLine, XCircle, Camera, User, Ticket } from 'lucide-react';
import { Button } from './ui/button';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const parseQR = (text) => {
  if (!text) return { type: null };
  const trimmed = text.trim();
  const tokenMatch = trimmed.match(/^RAFFLESRUS-USER:([A-Za-z0-9-]+)$/);
  if (tokenMatch) return { type: 'user', userId: tokenMatch[1] };
  const userMatch = trimmed.match(/\/scan\/user\/([A-Za-z0-9-]+)/);
  if (userMatch) return { type: 'user', userId: userMatch[1] };
  const redeemMatch = trimmed.match(/\/redeem\/([A-Za-z0-9]+)/);
  if (redeemMatch) return { type: 'reward', code: redeemMatch[1].toUpperCase() };
  // Fallback: treat raw string as a reward code
  return { type: 'reward', code: trimmed.toUpperCase() };
};

const QRScannerModal = ({ onClose }) => {
  const token = localStorage.getItem('token');
  const scannerRef = useRef(null);
  const runningRef = useRef(false);
  const [phase, setPhase] = useState('scanning'); // scanning | loading | reward | user
  const [info, setInfo] = useState(null); // reward info
  const [userData, setUserData] = useState(null); // { user, active_draws }
  const [selectedDraw, setSelectedDraw] = useState(null);
  const [error, setError] = useState(null);
  const [working, setWorking] = useState(false);
  const authHeader = { headers: { Authorization: `Bearer ${token}` } };

  const stopScanner = async () => {
    if (scannerRef.current && runningRef.current) {
      try {
        await scannerRef.current.stop();
        await scannerRef.current.clear();
      } catch (e) { /* ignore */ }
      runningRef.current = false;
    }
  };

  const handleDecoded = async (decodedText) => {
    if (phase !== 'scanning') return;
    setPhase('loading');
    await stopScanner();
    const parsed = parseQR(decodedText);
    try {
      if (parsed.type === 'user') {
        const res = await axios.get(`${API}/admin/user-lookup/${parsed.userId}`, authHeader);
        setUserData(res.data);
        setSelectedDraw(null);
        setError(null);
        setPhase('user');
      } else {
        const res = await axios.get(`${API}/admin/redeem/${parsed.code}`, authHeader);
        setInfo(res.data);
        setError(null);
        setPhase('reward');
      }
    } catch (e) {
      setError(e.response?.data?.detail || 'Invalid QR code');
      setPhase(parsed.type === 'user' ? 'user' : 'reward');
    }
  };

  const startScanner = async () => {
    setPhase('scanning');
    setInfo(null);
    setUserData(null);
    setSelectedDraw(null);
    setError(null);
    try {
      const scanner = new Html5Qrcode('qr-reader-region', { verbose: false });
      scannerRef.current = scanner;
      await scanner.start(
        { facingMode: 'environment' },
        { fps: 10, qrbox: { width: 240, height: 240 } },
        handleDecoded,
        () => {}
      );
      runningRef.current = true;
    } catch (e) {
      setError('Could not access camera. Please allow camera permission.');
      setPhase('reward');
    }
  };

  useEffect(() => {
    startScanner();
    return () => { stopScanner(); };
    // eslint-disable-next-line
  }, []);

  const confirmRedeem = async () => {
    setWorking(true);
    try {
      await axios.post(`${API}/admin/redeem/${info.code}`, {}, authHeader);
      toast.success('Reward redeemed & invalidated');
      setInfo({ ...info, redeemed: true });
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to redeem');
    } finally {
      setWorking(false);
    }
  };

  const issueTicket = async () => {
    if (!selectedDraw) return toast.error('Pick a draw first');
    setWorking(true);
    try {
      const res = await axios.post(
        `${API}/admin/scan/issue-ticket`,
        { user_id: userData.user.id, draw_id: selectedDraw },
        authHeader
      );
      const drawTitle = res.data.draw_title || userData.active_draws.find((d) => d.id === selectedDraw)?.title || 'draw';
      toast.success(`Ticket ${res.data.ticket_number} issued to ${userData.user.name}`);
      setUserData({ ...userData, issued: true, issuedDrawTitle: drawTitle });
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to issue ticket');
    } finally {
      setWorking(false);
    }
  };

  const scanAnother = async () => {
    await stopScanner();
    startScanner();
  };

  const close = async () => {
    await stopScanner();
    onClose();
  };

  return (
    <div className="fixed inset-0 z-50 flex items-end justify-center overflow-y-auto bg-black/80 backdrop-blur-sm sm:items-center sm:px-5 sm:py-6">
      <motion.div
        initial={{ opacity: 0, scale: 0.96 }}
        animate={{ opacity: 1, scale: 1 }}
        className="w-full max-w-md overflow-hidden rounded-t-3xl border border-white/[0.08] bg-[#18181b] pb-safe sm:my-auto sm:rounded-3xl"
        data-testid="qr-scanner-modal"
      >
        <div className="flex items-center justify-between px-5 py-4 border-b border-[#27272a]">
          <div className="flex items-center gap-2">
            <ScanLine className="w-5 h-5 text-[#EAB308]" />
            <h2 className="text-white font-heading font-bold">Scan QR Code</h2>
          </div>
          <button data-testid="close-scanner-btn" onClick={close} className="text-[#a1a1aa] hover:text-white">
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="p-5">
          {/* Scanning view */}
          <div className={phase === 'scanning' ? 'block' : 'hidden'}>
            <div id="qr-reader-region" className="rounded-2xl overflow-hidden bg-black mx-auto [&_video]:rounded-2xl" style={{ width: '100%' }} />
            <p className="text-center text-sm text-[#a1a1aa] mt-4 flex items-center justify-center gap-1.5">
              <Camera className="w-4 h-4" /> Scan a member's QR or a reward QR
            </p>
          </div>

          {/* Loading */}
          {phase === 'loading' && (
            <div className="py-12 flex flex-col items-center gap-3">
              <Loader2 className="w-8 h-8 text-[#EAB308] animate-spin" />
              <p className="text-[#a1a1aa] text-sm">Reading QR…</p>
            </div>
          )}

          {/* Reward flow */}
          {phase === 'reward' && (
            <div className="text-center" data-testid="scanner-reward-result">
              {error ? (
                <div data-testid="scanner-error">
                  <XCircle className="w-12 h-12 text-[#f43f5e] mx-auto mb-3" />
                  <p className="text-white font-medium mb-5">{error}</p>
                  <Button data-testid="scan-again-btn" onClick={scanAnother} className="w-full bg-[#EAB308] hover:bg-[#CA8A04] text-black font-bold rounded-full h-11">Scan Again</Button>
                </div>
              ) : info ? (
                <>
                  <div className="bg-[#EAB308]/15 w-14 h-14 rounded-2xl flex items-center justify-center mx-auto mb-4">
                    <Gift className="w-7 h-7 text-[#EAB308]" />
                  </div>
                  <p className="text-xs uppercase tracking-widest text-[#52525b]">Code {info.code}</p>
                  <h3 className="text-xl font-heading font-bold text-white mt-1 mb-2">{info.deal_text || 'Deal Reward'}</h3>
                  {info.poll_question && <p className="text-sm text-[#a1a1aa] mb-5">From poll: “{info.poll_question}”</p>}
                  {info.redeemed ? (
                    <div className="flex flex-col items-center gap-2 bg-[#27272a] rounded-2xl py-5 mb-3" data-testid="scanner-already-redeemed">
                      <XCircle className="w-8 h-8 text-[#f43f5e]" />
                      <p className="text-white font-bold">Already Redeemed</p>
                      {info.redeemed_at && <p className="text-xs text-[#52525b]">{new Date(info.redeemed_at).toLocaleString()}</p>}
                      <p className="text-xs text-[#a1a1aa]">This QR can no longer be used.</p>
                    </div>
                  ) : (
                    <Button data-testid="scanner-redeem-btn" onClick={confirmRedeem} disabled={working} className="w-full bg-[#10b981] hover:bg-[#059669] text-black font-bold rounded-full h-12 mb-3">
                      {working ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Check className="w-4 h-4 mr-2" /> Redeem & Invalidate</>}
                    </Button>
                  )}
                  <Button data-testid="scan-another-btn" onClick={scanAnother} variant="outline" className="w-full border-[#27272a] text-[#a1a1aa] hover:text-white rounded-full h-11">Scan Another</Button>
                </>
              ) : null}
            </div>
          )}

          {/* User → issue ticket flow */}
          {phase === 'user' && (
            <div className="text-center" data-testid="scanner-user-result">
              {error ? (
                <div data-testid="scanner-error">
                  <XCircle className="w-12 h-12 text-[#f43f5e] mx-auto mb-3" />
                  <p className="text-white font-medium mb-5">{error}</p>
                  <Button data-testid="scan-again-btn" onClick={scanAnother} className="w-full bg-[#EAB308] hover:bg-[#CA8A04] text-black font-bold rounded-full h-11">Scan Again</Button>
                </div>
              ) : userData ? (
                <>
                  <div className="bg-[#A855F7]/15 w-14 h-14 rounded-2xl flex items-center justify-center mx-auto mb-3">
                    <User className="w-7 h-7 text-[#A855F7]" />
                  </div>
                  <h3 className="text-xl font-heading font-bold text-white" data-testid="scanner-user-name">{userData.user.name}</h3>
                  <p className="text-sm text-[#a1a1aa] mb-5">{userData.user.whatsapp_number}</p>

                  {userData.issued ? (
                    <div className="flex flex-col items-center gap-2 bg-[#10b981]/10 border border-[#10b981]/30 rounded-2xl py-6 mb-3" data-testid="scanner-ticket-issued">
                      <Check className="w-9 h-9 text-[#10b981]" />
                      <p className="text-white font-bold">Ticket Issued!</p>
                      <p className="text-sm text-[#a1a1aa]">for {userData.issuedDrawTitle}</p>
                    </div>
                  ) : userData.active_draws.length === 0 ? (
                    <div className="bg-[#27272a] rounded-2xl py-6 px-4 mb-3">
                      <p className="text-[#a1a1aa] text-sm">No active draws available. Create a draw first.</p>
                    </div>
                  ) : (
                    <>
                      <p className="text-sm text-[#a1a1aa] mb-2 flex items-center justify-center gap-1.5">
                        <Ticket className="w-4 h-4 text-[#EAB308]" /> Give a ticket for:
                      </p>
                      <div className="space-y-2 mb-4 max-h-56 overflow-y-auto" data-testid="scanner-draw-list">
                        {userData.active_draws.map((d) => (
                          <button
                            key={d.id}
                            data-testid={`scanner-draw-${d.id}`}
                            onClick={() => setSelectedDraw(d.id)}
                            className={`w-full text-left px-4 py-3 rounded-xl border transition-colors ${
                              selectedDraw === d.id ? 'border-[#EAB308] bg-[#EAB308]/10' : 'border-[#27272a] bg-[#09090b] hover:border-[#52525b]'
                            }`}
                          >
                            <div className="text-white text-sm font-medium">{d.title}</div>
                            {d.prize_description && <div className="text-xs text-[#a1a1aa] mt-0.5">{d.prize_description}</div>}
                          </button>
                        ))}
                      </div>
                      <Button data-testid="scanner-issue-ticket-btn" onClick={issueTicket} disabled={working || !selectedDraw} className="w-full bg-[#EAB308] hover:bg-[#CA8A04] text-black font-bold rounded-full h-12 mb-3">
                        {working ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Ticket className="w-4 h-4 mr-2" /> Issue Ticket</>}
                      </Button>
                    </>
                  )}
                  <Button data-testid="scan-another-btn" onClick={scanAnother} variant="outline" className="w-full border-[#27272a] text-[#a1a1aa] hover:text-white rounded-full h-11">Scan Another</Button>
                </>
              ) : null}
            </div>
          )}
        </div>
      </motion.div>
    </div>
  );
};

export default QRScannerModal;
